/**
 * Saisie guidée des numéros du dossier apprenant.
 *
 * Ces fonctions vivent hors des écrans : le NIR et le téléphone se saisissent
 * aussi bien à l'inscription que dans le dossier, et deux formatages
 * divergents produiraient deux orthographes du même numéro en base.
 */

// ---------------------------------------------------------------------------
// Saisie guidée des numéros
// ---------------------------------------------------------------------------

/**
 * Les numéros se saisissent par groupes, comme ils sont lus à voix haute au
 * téléphone ou recopiés d'une carte Vitale. Le formatage est appliqué à la
 * frappe plutôt que contrôlé à l'envoi : une suite de quinze chiffres collés
 * ne se relit pas, et c'est en la relisant qu'on repère l'inversion que la clé
 * de contrôle signalera trop tard, une fois la personne raccrochée.
 *
 * Les groupes sont ceux du CERFA : sexe (1), année (2), mois (2),
 * département (2), commune (3), ordre (3), clé (2).
 */
const GROUPES_NIR = [1, 2, 2, 2, 3, 3, 2];

/** 15 caractères et 6 espaces : la borne de l'attribut `maxLength`. */
export const LONGUEUR_NIR_AFFICHEE =
  GROUPES_NIR.reduce((total, groupe) => total + groupe, 0) + GROUPES_NIR.length - 1;

/**
 * Borne de saisie du téléphone, en caractères affichés.
 *
 * Un numéro local tient en 10 chiffres et 4 espaces. La forme internationale
 * en admet davantage — jusqu'à 15 chiffres selon E.164 — et une borne unique
 * couperait la saisie d'un numéro mauricien en cours de frappe. La borne suit
 * donc la forme engagée par le `+` initial.
 */
export function longueurTelephoneAffichee(saisie: string | undefined): number {
  return (saisie ?? '').trim().startsWith('+') ? 22 : 14;
}

/** Chiffres d'une saisie, tronqués à la longueur utile. */
export function chiffres(saisie: string, maximum: number): string {
  return saisie.replace(/\D/g, '').slice(0, maximum);
}

/**
 * Caractères significatifs d'un NIR : des chiffres, sauf au septième rang.
 *
 * Le département occupe les rangs 6 et 7 (`2A`, `2B` en Corse) : c'est la
 * seconde de ces deux places, et elle seule, qui admet une lettre. Ailleurs,
 * la lettre vient d'une faute de frappe, et la laisser passer ferait échouer
 * le contrôle de clé sans dire pourquoi.
 */
export function chiffresNir(saisie: string | undefined): string {
  let retenus = '';
  for (const caractere of (saisie ?? '').toUpperCase()) {
    const position = retenus.length;
    if (position === 15) break;
    const corse = position === 6 && (caractere === 'A' || caractere === 'B');
    if (/[0-9]/.test(caractere) || corse) retenus += caractere;
  }
  return retenus;
}

/** Regroupe la saisie et la bloque à 15 caractères. */
export function formaterNir(saisie: string): string {
  const retenus = chiffresNir(saisie);
  const morceaux: string[] = [];
  let curseur = 0;
  for (const groupe of GROUPES_NIR) {
    if (curseur >= retenus.length) break;
    morceaux.push(retenus.slice(curseur, curseur + groupe));
    curseur += groupe;
  }
  return morceaux.join(' ');
}

/** Un NIR commencé mais pas fini : signalé à l'écran, jamais bloquant. */
export function nirIncomplet(saisie: string | undefined): boolean {
  const longueur = chiffresNir(saisie).length;
  return longueur > 0 && longueur < 15;
}

/**
 * Numéro de téléphone regroupé par deux, comme il se dicte.
 *
 * Deux formes coexistent dans les dossiers : la forme locale à dix chiffres
 * (`06 92 55 12 09`) et la forme internationale d'un numéro mauricien ou
 * malgache, qu'il ne faut surtout pas tronquer à dix — `numeroSmsE164` côté
 * API accepte les plans qu'elle ne connaît pas, précisément pour ne pas
 * écarter ces apprenants. Le `+` initial fait donc lever la borne.
 */
export function formaterTelephone(saisie: string): string {
  const international = saisie.trim().startsWith('+');
  const numeros = saisie.replace(/\D/g, '').slice(0, international ? 15 : 10);
  if (!international) return (numeros.match(/\d{1,2}/g) ?? []).join(' ');

  // L'indicatif reste d'un seul tenant : découpé par deux comme le reste, il
  // se lirait « +26 26 92… » et ne se reconnaîtrait plus. Trois chiffres
  // couvrent les plans de la zone (+262 La Réunion, +230 Maurice,
  // +261 Madagascar, +269 Comores) ; un indicatif à un ou deux chiffres reste
  // lisible collé au reste, ce qui vaut mieux qu'une coupure au mauvais rang.
  const indicatif = numeros.slice(0, 3);
  const reste = (numeros.slice(3).match(/\d{1,2}/g) ?? []).join(' ');
  return `+${indicatif}${reste === '' ? '' : ` ${reste}`}`;
}

/** Numéro local commencé mais pas fini. La forme internationale est libre. */
export function telephoneIncomplet(saisie: string | undefined): boolean {
  const valeur = saisie ?? '';
  if (valeur.trim().startsWith('+')) return false;
  const longueur = valeur.replace(/\D/g, '').length;
  return longueur > 0 && longueur < 10;
}
